import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Compass, Sparkles, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button, PageHeader, Spinner } from '../../ui';
import { MessageBubble } from '../Chat/MessageBubble';
import { Composer } from '../Chat/Composer';
import { EntityRefLinks } from '../common/EntityRefLinks';
import { CitationsPanel } from '../Chat/CitationsPanel';
import { useAskVector } from '../../features/intelligence/useAskVector';
import type { ChatMessage } from '../../features/chat/types';
import type { AskTurn } from '../../features/intelligence/types';

const EXAMPLES = [
  "What's the status of our top goals?",
  'Who is working on the checkout service and why?',
  'What decisions did we make about the data model?',
  'What is blocking the team right now?',
];

/** Adapts an Ask Vector turn to the shape the shared Chat MessageBubble renders. */
function toChatMessage(turn: AskTurn): ChatMessage {
  return {
    id: turn.id,
    role: turn.role,
    content: turn.content,
    createdAt: turn.createdAt,
    ...(turn.error ? { error: true } : {}),
  };
}

/**
 * Ask Vector — a conversational box that answers questions about the team by
 * retrieving the most relevant entities CLIENT-SIDE (RAG-lite), calling REAL
 * Bedrock, and rendering the answer WITH source links back to the exact Goal /
 * Story / Workstream / Daily Update / Document / Decision it used. Reuses the
 * Chat design (message bubbles + composer).
 */
export function AskVectorPage() {
  const { turns, loading, asking, error, ask, clear } = useAskVector();

  const location = useLocation();
  const navigate = useNavigate();

  // A landing-page hero (or any caller) can deep-link a question in via router
  // state; we run it exactly once, after team context has finished loading,
  // then strip the state so a refresh / back-navigation doesn't re-ask it.
  const initialQuestion = (
    location.state as { initialQuestion?: string } | null
  )?.initialQuestion;
  const autoAskedRef = useRef(false);
  useEffect(() => {
    if (autoAskedRef.current) return;
    if (!initialQuestion || loading) return;
    autoAskedRef.current = true;
    void ask(initialQuestion);
    navigate(location.pathname, { replace: true, state: null });
  }, [initialQuestion, loading, ask, navigate, location.pathname]);

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, asking]);

  return (
    <div>
      <PageHeader
        icon={<Compass className="h-5 w-5" />}
        title="Ask Vector"
        description="Ask about the team's goals, work, changes, docs, and decisions. Answers cite the exact entities they came from."
        actions={
          turns.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clear}
              leftIcon={<RotateCcw className="h-4 w-4" />}
            >
              New question
            </Button>
          ) : undefined
        }
      />

      <div className="flex h-[calc(100vh-13rem)] flex-col overflow-hidden rounded-2xl border border-surface-border bg-surface-1">
        <div className="flex-1 overflow-y-auto px-4 py-5">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner label="Loading team context" />
            </div>
          ) : error ? (
            <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500/10 text-red-300">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h2 className="text-base font-semibold text-zinc-100">
                Couldn't load team context
              </h2>
              <p className="mt-1.5 text-sm text-zinc-500">{error}</p>
            </div>
          ) : turns.length === 0 ? (
            <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-2 text-accent">
                <Sparkles className="h-6 w-6" />
              </div>
              <h2 className="text-base font-semibold text-zinc-100">
                Ask about your team
              </h2>
              <p className="mt-1.5 text-sm text-zinc-500">
                Vector retrieves the most relevant goals, workstreams, stories,
                documents and decisions, then answers — with links back to each
                source. It only uses what your team has recorded.
              </p>
              <div className="mt-5 grid w-full gap-2 text-left">
                {EXAMPLES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => void ask(q)}
                    className="rounded-xl border border-surface-border bg-surface-2 px-3.5 py-2.5 text-sm text-zinc-300 transition-colors hover:border-accent/40 hover:bg-surface-3"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex max-w-3xl flex-col gap-4">
              {turns.map((turn) => (
                <div key={turn.id} className="flex flex-col gap-2">
                  <MessageBubble message={toChatMessage(turn)} />
                  {turn.role === 'assistant' && !turn.error && (
                    <div className="flex flex-col gap-2 pl-11">
                      {turn.kbCitations && turn.kbCitations.length > 0 && (
                        <CitationsPanel citations={turn.kbCitations} />
                      )}
                      {turn.sources.length > 0 && (
                        <div className="rounded-xl border border-surface-border bg-surface-2/60 px-3.5 py-3">
                          <EntityRefLinks
                            refs={turn.sources}
                            heading={`Team entities (${turn.sources.length})`}
                            grouped
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {asking && (
                <div className="flex items-center gap-2 pl-11 text-sm text-zinc-500">
                  <Spinner label="Retrieving and answering" />
                  <span className="animate-fade-in">Retrieving context…</span>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <Composer sending={asking} onSend={(text) => void ask(text)} />
      </div>
    </div>
  );
}

export default AskVectorPage;
